import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { format } from 'date-fns';
import * as _ from 'lodash';
import { Observable, map } from 'rxjs';

import { Attachment } from '@digital-platform/attachments/domain';
import { DocusignData } from '@digital-platform/docusign/domain';
import { SignNowDocument } from '@digital-platform/sign-now/domain';
import { LoginSessionFacadeService } from '@digital-platform/users/services';
import {
  AdditionalSupplement,
  ChangeRequestDetails,
  LabAnalyticalAttachment,
  RecertificationDetails,
  SafetyDataSheet,
  SpecialWasteProfile,
  WasteProfileCardTypes,
} from '@digital-platform/waste-profiles/domain';
import { WasteProfilesFacadeService } from '@digital-platform/waste-profiles/services';

enum WasteProfileAttachmentType {
  CHANGE_REQUEST = 'Change Request',
  RECERTIFICATION = 'Recertification',
  WASTE_PROFILE = 'Waste Profile',
}

interface WasteProfileAttachment extends Attachment {
  type?: WasteProfileAttachmentType;
}
@Component({
  selector: 'digital-platform-waste-profile-attachments-log',
  templateUrl: './waste-profile-attachments-log.component.html',
  styleUrls: ['./waste-profile-attachments-log.component.scss'],
})
export class WasteProfileAttachmentsLogComponent implements OnChanges {
  @Input() wasteProfile: SpecialWasteProfile;
  @Input() accessFormState: any;
  @Output()
  deleteAdditionalAdminSupplementLog: EventEmitter<SpecialWasteProfile> = new EventEmitter<SpecialWasteProfile>();
  @Output() deleteStateAdminAttachmentLog: EventEmitter<SpecialWasteProfile> =
    new EventEmitter<SpecialWasteProfile>();
  @Output() deleteInternalOnlyAttachmentLog: EventEmitter<SpecialWasteProfile> =
    new EventEmitter<SpecialWasteProfile>();
  @Output() deleteThirdPartyAttachmentLog: EventEmitter<SpecialWasteProfile> =
    new EventEmitter<SpecialWasteProfile>();

  public signNowDocuments: SignNowDocument[] = [];
  public docusignData: DocusignData[] = [];
  public labAttachments: WasteProfileAttachment[] = [];
  public safetyDataSheets: WasteProfileAttachment[] = [];
  public additionalSupplements: WasteProfileAttachment[] = [];
  public additionalAdminSupplements: WasteProfileAttachment[] = [];
  public stateInternalAdminAttachments: Attachment[] = [];
  public stateAttachments: Attachment[] = [];
  public internalAdminAttachments: Attachment[] = [];
  public internalOnlyAttachments: Attachment[] = [];
  public unsignedDocuments: DocusignData[] = [];
  public hasUpdateWasteProfilePermission$: Observable<boolean>;
  public hasViewInternalAttachmentPermission$: Observable<boolean>;
  public hasViewManifest$: Observable<boolean>;
  public deleteFile: {
    additionalAdminSupplement:
      | AdditionalSupplement
      | Attachment
      | { url: string; uploadDate: Date };
    supplementName: string | undefined;
  };
  @Output() delete = new EventEmitter<WasteProfileCardTypes>();
  formState: any;
  removeDialogRef: MatDialogRef<WasteProfileAttachmentsLogComponent>;

  ngOnChanges(): void {
    this.getAttachmentUrls();
    this.getSignNowDocuments();
    this.getDocusignDocuments();
  }

  constructor(
    private dialog: MatDialog,
    private facade: WasteProfilesFacadeService,
    private loginSessionFacade: LoginSessionFacadeService
  ) {
    this.hasUpdateWasteProfilePermission$ =
      this.loginSessionFacade.loginSession$.pipe(
        map((res) =>
          res?.user?.permissions?.includes('update:waste_profile_metadata')
        )
      );
    this.hasViewInternalAttachmentPermission$ =
      this.loginSessionFacade.loginSession$.pipe(
        map((res) =>
          res?.user?.permissions?.includes('view:internal_attachments')
        )
      );
    this.hasViewManifest$ = this.loginSessionFacade.loginSession$.pipe(
      map((res) => res?.user?.permissions?.includes('view:manifest'))
    );
  }

  public get hasThirdPartyUrl() {
    if (this.wasteProfile?.thirdPartyAuth?.docusignData?.envelopeId) {
      return (
        '/api/docusign/open/' +
        this.wasteProfile?.thirdPartyAuth?.docusignData?.envelopeId
      );
    } else {
      if (this.wasteProfile?.thirdPartyAuth?.signNowDocumentUrl) {
        const splitUrl =
          this.wasteProfile?.thirdPartyAuth?.signNowDocumentUrl.split('/');
        const id = splitUrl[splitUrl.length - 1];
        return '/api/sign-now/share/' + id;
      } else {
        return null;
      }
    }
  }

  public get hasThirdPartyAttachment() {
    const url = this.wasteProfile?.thirdPartyAuthAttachment?.fileUrl;
    if (url) {
      return {
        url: this.wasteProfile?.thirdPartyAuthAttachment?.fileUrl,
        uploadDate: this.wasteProfile?.thirdPartyAuthAttachment?.uploadDate,
      };
    }
    return url;
  }

  public get hasAdminThirdPartyUrl() {
    if (this.wasteProfile?.thirdPartyAuthAdminAttachment?.fileUrl) {
      return {
        url: this.wasteProfile?.thirdPartyAuthAdminAttachment?.fileUrl,
        uploadDate:
          this.wasteProfile?.thirdPartyAuthAdminAttachment?.uploadDate,
      };
    } else {
      return false;
    }
  }

  getDocusignDocuments = () => {
    const docusignData: DocusignData[] = [];

    // first, let's handle any existing docusignData object on the profile
    const {
      docusignData: profileDocument,
      pendingChangeRequest,
      pendingRecertification,
    } = this.wasteProfile;

    // if we have a signed document
    if (profileDocument?.envelopeId && profileDocument.complete) {
      docusignData.push(this.handleProfileSignedDocument(this.wasteProfile));
    }

    //handle new approval offline submission
    if (!profileDocument?.envelopeId && profileDocument?.signingLink) {
      docusignData.push(
        this.handleProfileSignedOfflineDocument(this.wasteProfile)
      );
    }
    //handle offline recertify, change, + recertify w change
    if (
      (!pendingChangeRequest?.docusignData?.envelopeId &&
        pendingChangeRequest?.docusignData?.signingLink) ||
      (!pendingRecertification?.docusignData?.envelopeId &&
        pendingRecertification?.docusignData?.signingLink)
    ) {
      docusignData.push(
        ...this.handleOfflienRecertChangeSignedDocuments(this.wasteProfile)
      );
    }

    // handle recert and recert with change documents
    if (
      this.hasADocument(pendingChangeRequest) ||
      this.hasADocument(pendingRecertification)
    ) {
      docusignData.push(
        ...this.handleRecertChangeSignedDocuments(this.wasteProfile)
      );
    }

    this.docusignData = docusignData.filter(
      (document) => document.complete === true
    );

    this.unsignedDocuments = docusignData.filter(
      (document) => document.complete === false
    );
  };

  hasADocument = (
    changeOrRecert: RecertificationDetails | ChangeRequestDetails
  ) => !!changeOrRecert?.docusignData?.envelopeId;

  handleProfileSignedDocument = (
    wasteProfile: SpecialWasteProfile
  ): DocusignData => {
    const profileDocument = wasteProfile?.docusignData;

    const namePrefix = 'Waste Profile';
    const name = profileDocument?.documentName || 'Signed Document';
    const envelopeId = profileDocument?.envelopeId;

    const { documentName, signingLink } = this.getDocNameAndSigningLink(
      namePrefix,
      name,
      envelopeId
    );

    return {
      ...profileDocument,
      documentName,
      signingLink,
    };
  };

  handleProfileSignedOfflineDocument = (
    wasteProfile: SpecialWasteProfile
  ): DocusignData => {
    const profileDocument = wasteProfile?.docusignData;

    const namePrefix = 'Waste Profile';
    const name = 'Signed Document';
    const signedPdfLink = profileDocument?.signingLink;

    const { documentName, signingLink } = this.getOfflineDocNameAndSigningLink(
      namePrefix,
      name,
      signedPdfLink
    );

    return {
      ...profileDocument,
      documentName,
      signingLink,
    };
  };

  handleOfflienRecertChangeSignedDocuments = (
    wasteProfile: SpecialWasteProfile
  ): DocusignData[] => {
    // handles offline recertify, change, + recertify w change
    const {
      isChange,
      isRecertification,
      pendingChangeRequest,
      pendingRecertification,
    } = wasteProfile;

    // if a recert with change, not sure if this scenario works in offline submission.
    if (
      isRecertification &&
      isChange &&
      pendingChangeRequest &&
      pendingRecertification?.hasChangeRecertification
    ) {
      // for recert with change, we only have to worry about the recert document
      const { docusignData: recertDocument } = pendingRecertification;
      const name = recertDocument.documentName || 'Signed Recertification';
      const namePrefix = 'Recertification With Change Request';
      const signedPdfLink = recertDocument?.signingLink;

      const { documentName, signingLink } =
        this.getOfflineDocNameAndSigningLink(namePrefix, name, signedPdfLink);
      const updatedDocusignData = {
        ...recertDocument,
        documentName,
        signingLink,
      };
      return [updatedDocusignData];
    }

    // if we get here, then we have a regular recert and/or change
    const docusignData = [];
    // handle recert document
    if (
      !pendingRecertification?.docusignData?.envelopeId &&
      pendingRecertification?.docusignData?.signingLink
    ) {
      const recertDocument = pendingRecertification.docusignData;
      const name = recertDocument.documentName || 'Signed Recertification';
      const namePrefix = 'Recertification';
      const signedPdfLink = recertDocument?.signingLink;

      const { documentName, signingLink } =
        this.getOfflineDocNameAndSigningLink(namePrefix, name, signedPdfLink);
      const updatedDocusignData = {
        ...recertDocument,
        documentName,
        signingLink,
      };
      docusignData.push(updatedDocusignData);
    }

    // handle change document
    if (
      !pendingChangeRequest?.docusignData?.envelopeId &&
      pendingChangeRequest?.docusignData?.signingLink
    ) {
      const changeDocument = pendingChangeRequest.docusignData;
      const name = changeDocument.documentName || 'Signed Change Request';
      const namePrefix = 'Change Request';
      const signedPdfLink = changeDocument?.signingLink;

      const { documentName, signingLink } =
        this.getOfflineDocNameAndSigningLink(namePrefix, name, signedPdfLink);
      const updatedDocusignData = {
        ...changeDocument,
        documentName,
        signingLink,
      };
      docusignData.push(updatedDocusignData);
    }

    return docusignData;
  };

  handleRecertChangeSignedDocuments = (
    wasteProfile: SpecialWasteProfile
  ): DocusignData[] => {
    const {
      isChange,
      isRecertification,
      pendingChangeRequest,
      pendingRecertification,
    } = wasteProfile;

    // if a recert with change
    if (
      isRecertification &&
      isChange &&
      pendingChangeRequest &&
      pendingRecertification?.hasChangeRecertification
    ) {
      // for recert with change, we only have to worry about the recert document
      const { docusignData: recertDocument } = pendingRecertification;
      const name = recertDocument.documentName || 'Signed Recertification';
      const namePrefix = 'Recertification With Change Request';
      const envelopeId = recertDocument.envelopeId;
      const { documentName, signingLink } = this.getDocNameAndSigningLink(
        namePrefix,
        name,
        envelopeId
      );
      const updatedDocusignData = {
        ...recertDocument,
        documentName,
        signingLink,
      };
      return [updatedDocusignData];
    }

    // if we get here, then we have a regular recert and/or change
    const docusignData = [];
    // handle recert document
    if (pendingRecertification?.docusignData?.envelopeId) {
      const recertDocument = pendingRecertification.docusignData;
      const name = recertDocument.documentName || 'Signed Recertification';
      const namePrefix = 'Recertification';
      const envelopeId = recertDocument.envelopeId;
      const { documentName, signingLink } = this.getDocNameAndSigningLink(
        namePrefix,
        name,
        envelopeId
      );
      const updatedDocusignData = {
        ...recertDocument,
        documentName,
        signingLink,
      };
      docusignData.push(updatedDocusignData);
    }

    // handle change document
    if (pendingChangeRequest?.docusignData?.envelopeId) {
      const changeDocument = pendingChangeRequest.docusignData;
      const name = changeDocument.documentName || 'Signed Change Request';
      const namePrefix = 'Change Request';
      const envelopeId = changeDocument.envelopeId;
      const { documentName, signingLink } = this.getDocNameAndSigningLink(
        namePrefix,
        name,
        envelopeId
      );
      const updatedDocusignData = {
        ...changeDocument,
        documentName,
        signingLink,
      };
      docusignData.push(updatedDocusignData);
    }

    return docusignData;
  };

  getDocNameAndSigningLink = (
    namePrefix: string,
    name: string,
    envelopeId: string
  ): { documentName: string; signingLink: string } => {
    const documentName = `${namePrefix}: ${name}`;
    const signingLink = '/api/docusign/open/' + envelopeId;
    return { documentName, signingLink };
  };

  getOfflineDocNameAndSigningLink = (
    namePrefix: string,
    name: string,
    signedPdfLink: string
  ): { documentName: string; signingLink: string } => {
    const documentName = `${namePrefix}: ${name}`;
    const signingLink = signedPdfLink;
    return { documentName, signingLink };
  };

  getSignNowDocuments = () => {
    const signNowDocuments: SignNowDocument[] = [];

    if (this.wasteProfile?.signNowDocument) {
      let profileDocument = this.wasteProfile?.signNowDocument;
      if (!profileDocument.name) {
        profileDocument = {
          ...profileDocument,
          name: 'Signed Document',
        };
      }

      if (profileDocument?.url) {
        profileDocument = {
          ...profileDocument,
          url: profileDocument.url,
        };
      }

      signNowDocuments.push({
        ...profileDocument,
        name: `Waste Profile: ${profileDocument.name}`,
      });
    }

    if (this.wasteProfile?.pendingChangeRequest) {
      const { pendingChangeRequest } = this.wasteProfile;

      const { signNowDocumentId, signNowDocument: changeDocument } =
        pendingChangeRequest;

      if (
        signNowDocumentId &&
        changeDocument?.id &&
        signNowDocumentId !== changeDocument?.id
      ) {
        // there's an older record and it's different than the other document, show it
        const name =
          pendingChangeRequest?.signNowDocumentName || 'Signed Change Request';
        signNowDocuments.push({
          id: pendingChangeRequest?.signNowDocumentId,
          url: pendingChangeRequest?.signNowDocumentUrl,
          name: `Change Request: ${name}`,
          isComplete:
            pendingChangeRequest.isSigned ||
            pendingChangeRequest.signNowDocument?.isComplete,
        });
      }

      if (pendingChangeRequest?.signNowDocument?.isComplete) {
        const name = changeDocument.name || 'Signed Change Request';

        signNowDocuments.push({
          ...changeDocument,
          name: `Change Request: ${name}`,
        });
      }
    }

    if (this.wasteProfile?.pendingRecertification?.signNowDocument) {
      const signNowDocument =
        this.wasteProfile?.pendingRecertification?.signNowDocument;
      const name = signNowDocument.name || 'Signed Recertification';
      if (
        this.wasteProfile?.pendingRecertification?.signNowDocument?.isComplete
      ) {
        signNowDocuments.push({
          ...signNowDocument,
          name:
            this.wasteProfile?.pendingChangeRequest?.changeReason &&
            !this.wasteProfile?.pendingChangeRequest?.signNowDocument?.url
              ? `Recertification With Change Request: ${name}`
              : `Recertification: ${name}`,
        });
      }
    }

    this.signNowDocuments = signNowDocuments.filter(
      (document) => document.isComplete === true
    );
  };

  getAttachmentUrls = () => {
    this.labAttachments = [
      ...this.getAttachments(
        this.wasteProfile?.labAnalyticalAttachments,
        WasteProfileAttachmentType.WASTE_PROFILE
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingRecertification?.labAnalyticalAttachments,
        WasteProfileAttachmentType.RECERTIFICATION
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingChangeRequest?.labAnalyticalAttachments,
        WasteProfileAttachmentType.CHANGE_REQUEST
      ),
    ];

    this.safetyDataSheets = [
      ...this.getAttachments(
        this.wasteProfile?.safetyDataSheets,
        WasteProfileAttachmentType.WASTE_PROFILE
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingRecertification?.safetyDataSheets,
        WasteProfileAttachmentType.RECERTIFICATION
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingChangeRequest?.safetyDataSheets,
        WasteProfileAttachmentType.CHANGE_REQUEST
      ),
    ];

    this.additionalAdminSupplements = [
      ...this.getAttachments(
        this.wasteProfile?.additionalAdminSupplements,
        'Additional Supplements'
      ),
    ];

    this.additionalSupplements = [
      ...this.getAttachments(
        this.wasteProfile?.additionalSupplements,
        WasteProfileAttachmentType.WASTE_PROFILE
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingRecertification?.additionalSupplements,
        WasteProfileAttachmentType.RECERTIFICATION
      ),
      ...this.getAttachments(
        this.wasteProfile?.pendingChangeRequest?.additionalSupplements,
        WasteProfileAttachmentType.CHANGE_REQUEST
      ),
    ];

    this.stateInternalAdminAttachments = [
      ...this.getDirectAttachments(
        this.wasteProfile?.stateInternalAdminAttachments,
        'State Required Documents'
      ),
    ];
    this.stateAttachments = [
      ...this.getDirectAttachments(
        this.wasteProfile?.stateAttachments,
        WasteProfileAttachmentType.WASTE_PROFILE
      ),
      ...this.getDirectAttachments(
        this.wasteProfile?.pendingRecertification?.stateAttachments,
        WasteProfileAttachmentType.RECERTIFICATION
      ),
      ...this.getDirectAttachments(
        this.wasteProfile?.pendingChangeRequest?.stateAttachments,
        WasteProfileAttachmentType.CHANGE_REQUEST
      ),
    ];

    this.internalAdminAttachments = [
      ...this.getDirectAttachments(
        this.wasteProfile?.internalAdminAttachments,
        'Internal Only Attachment'
      ),
    ];
    this.internalOnlyAttachments = [
      ...this.getDirectAttachments(
        this.wasteProfile?.internalOnlyAttachments,
        WasteProfileAttachmentType.WASTE_PROFILE
      ),
      ...this.getDirectAttachments(
        this.wasteProfile?.pendingRecertification?.internalOnlyAttachments,
        WasteProfileAttachmentType.RECERTIFICATION
      ),
    ];
  };

  getAttachments(
    attachments:
      | LabAnalyticalAttachment[]
      | AdditionalSupplement[]
      | SafetyDataSheet[],
    type: string = WasteProfileAttachmentType.WASTE_PROFILE
  ) {
    return (
      attachments
        ?.map((a) => _.merge(a?.attachment, { type }))
        .filter((f) => f?.fileName) || []
    );
  }

  getDirectAttachments(
    attachments: Attachment[],
    type: string = WasteProfileAttachmentType.WASTE_PROFILE
  ) {
    return (
      attachments
        ?.map((a) => _.merge(a, { type }))
        .filter((f) => f?.fileName) || []
    );
  }

  displayDate = (uploadDate) => {
    if (uploadDate) {
      const uploadDateObject = new Date(uploadDate);
      if (uploadDateObject) {
        return ' - ' + format(uploadDateObject, 'MMM d yyyy');
      }
    }
    return '';
  };

  public openDialog(
    deleteDialog,
    additionalAdminSupplement,
    supplementName
  ): void {
    this.deleteFile = { additionalAdminSupplement, supplementName };
    this.removeDialogRef = this.dialog.open(deleteDialog, {
      width: '650px',
      height: '400px',
    });
  }

  deleteWasteProfile() {
    if (this.deleteFile.supplementName === 'adminSupplement') {
      this.onAdditionalAdminSupplementDeleted(
        this.deleteFile.additionalAdminSupplement as AdditionalSupplement
      );
    } else if (this.deleteFile.supplementName === 'stateAttachmentName') {
      this.onStateAdminAttachmentDeleted(
        this.deleteFile.additionalAdminSupplement as Attachment
      );
    } else if (this.deleteFile.supplementName === 'internalOnlyName') {
      this.onInternalOnlyAttachmentDeleted(
        this.deleteFile.additionalAdminSupplement as Attachment
      );
    } else {
      this.thirdPartyAttachmentDeleted(
        this.deleteFile.additionalAdminSupplement as {
          url: string;
          uploadDate: Date;
        }
      );
    }
  }

  onAdditionalAdminSupplementDeleted(fileData) {
    const { additionalAdminSupplements: asPrev } = this.wasteProfile;
    const additionalAdminSupplements = asPrev.filter(
      (as) => as?.attachment.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      additionalAdminSupplements,
    };
    if (this.removeDialogRef) {
      this.removeDialogRef.close();
    }
    this.deleteAdditionalAdminSupplementLog.emit(this.wasteProfile);
  }

  onStateAdminAttachmentDeleted(fileData: Attachment) {
    const { stateInternalAdminAttachments: saPrev } = this.wasteProfile;
    const stateInternalAdminAttachments = saPrev.filter(
      (sa) => sa.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      stateInternalAdminAttachments,
    };
    if (this.removeDialogRef) {
      this.removeDialogRef.close();
    }
    this.deleteStateAdminAttachmentLog.emit(this.wasteProfile);
  }

  onInternalOnlyAttachmentDeleted(fileData: Attachment) {
    const { internalAdminAttachments: saPrev } = this.wasteProfile;
    const internalAdminAttachments = saPrev.filter(
      (ia) => ia.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      internalAdminAttachments,
    };
    if (this.removeDialogRef) {
      this.removeDialogRef.close();
    }
    this.deleteInternalOnlyAttachmentLog.emit(this.wasteProfile);
  }

  thirdPartyAttachmentDeleted(fileData: { url: string; uploadDate: Date }) {
    if (fileData?.url) {
      this.wasteProfile = {
        ...this.wasteProfile,
        thirdPartyAuthAdminAttachment: null,
      };
      if (this.removeDialogRef) {
        this.removeDialogRef.close();
      }
      this.deleteThirdPartyAttachmentLog.emit(this.wasteProfile);
    }
  }
}
